import React, { useMemo } from 'react';
import { Box, Dialog, DialogContent, Button, Divider } from '@material-ui/core';

import { useCalendarAutocomplete } from 'src/hooks/autocompletes/useCalendarAutocomplete';
import { useSelector } from 'react-redux';
import {
  calendarDates,
  calendarOptions,
  tripData,
} from 'src/redux/Calendar/sliceCalendar';
import { TicketsBulkForm } from './TicketsBulkForm';
import { OptionsBulkForm } from './OptionsBulkForm';
import { SchedulesBulkForm } from './SchedulesBulkForm';
import { customFormat } from 'src/helpers/DateFormat';
import { normalizeTime } from 'src/helpers/calendar/normalizeTime';
import { Spiner } from '../../Loaders/Spiner/Spiner';
import { useStyles } from './styles';

export const CalendarBulkDialog = ({
  open,
  onClose,
  applyChanges,
  updateLoading,
  state,
  setState,
}: {
  open: boolean;
  onClose: () => void;
  applyChanges: () => void;
  state: any;
  updateLoading: boolean;
  setState: React.Dispatch<any>;
}) => {
  const classes = useStyles();
  const { tripList } = useCalendarAutocomplete();
  const { rate_id, type, option_type, ticket_type, trip_id, schedulesIds } =
    useSelector(calendarOptions);
  const data = useSelector(tripData);
  const trip = tripList.find((i) => i.id === trip_id);
  const comission = trip?.comission || '';
  const dates = useSelector(calendarDates);
  const schedules = (schedulesIds || [])
    .map((i) => {
      const currentSchedule = data?.schedules.find((t) => t.id === +i);
      return currentSchedule
        ? normalizeTime(currentSchedule.hour, currentSchedule.minute)
        : null;
    })
    .filter((i) => !!i);

  const content = useMemo(() => {
    const partnerName = trip?.partner_name || '';
    const tripName = data?.name || '';
    const rateName = data?.rates.find((i) => i.id === rate_id)?.name || '';
    const ticketName =
      data?.tickets.find((i) => i.id === ticket_type)?.name || '';
    const optionName =
      data?.additional_options.find((i) => i.id === option_type)?.name || '';

    return (
      <Box mr="auto">
        <Box style={{ fontSize: '16px' }}>
          {type === 'ticket' && (
            <>
              Batch editing of the <strong>"{ticketName}"</strong> ticket
            </>
          )}
          {type === 'option' && (
            <>
              Batch editing of the additional option{' '}
              <strong>"{optionName}"</strong>
            </>
          )}
          {type === 'schedule' && (
            <>
              Batch editing of the <strong>schedules</strong>
            </>
          )}
        </Box>
        <Box>
          dates:{' '}
          <strong>{customFormat(dates[0].date_from, 'MMMM DD YYYY')}</strong>
          &nbsp;-&nbsp;
          <strong>{customFormat(dates[0].date_to, 'MMMM DD YYYY')}</strong>
        </Box>
        {!!schedules.length && type !== 'option' && (
          <Box>
            schedules:{' '}
            {schedules.map((i, idx) => (
              <>
                {!!idx && ', '}
                <strong>{i}</strong>
              </>
            ))}
          </Box>
        )}

        <Box>
          trip: <strong>{tripName}</strong>
          {!!rateName && <>, rate: {rateName}</>}
        </Box>
      </Box>
    );
  }, [
    data?.additional_options,
    data?.name,
    data?.rates,
    data?.tickets,
    dates,
    option_type,
    rate_id,
    schedules,
    ticket_type,
    trip?.partner_name,
    type,
  ]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description">
      <Box
        display="flex"
        justifyContent="flex-end"
        flexWrap="wrap"
        gridGap="16px"
        py={2}
        px={3}
        alignItems="center">
        <>{content}</>
        <Button
          style={{ marginRight: '8px' }}
          variant="outlined"
          onClick={onClose}
          color="default"
          className={classes.cancelButton}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={applyChanges}
          className={classes.saveButton}>
          Make Batch Changes
        </Button>
      </Box>
      <Divider variant="fullWidth" />
      <DialogContent style={{ padding: 0 }}>
        {updateLoading && <Spiner />}
        {type === 'ticket' && (
          <TicketsBulkForm
            comission={comission}
            state={state}
            setState={setState}
          />
        )}
        {type === 'option' && (
          <OptionsBulkForm state={state} setState={setState} />
        )}
        {type === 'schedule' && (
          <SchedulesBulkForm state={state} setState={setState} />
        )}
      </DialogContent>
    </Dialog>
  );
};
